"use client";

import * as React from "react";
import { cn } from "../../lib";

export interface AnimatedCircularProgressBarProps {
	max?: number;
	min?: number;
	value: number;
	gaugePrimaryColor: string;
	gaugeSecondaryColor: string;
	className?: string;
	transitionLength?: number;
	textVisible?: boolean;
}

export function AnimatedCircularProgressBar({
	max = 100,
	min = 0,
	value = 0,
	gaugePrimaryColor,
	gaugeSecondaryColor,
	className,
	transitionLength = 200,
	textVisible = true,
}: AnimatedCircularProgressBarProps) {
	const circleRadius = 45;
	const circumference = 2 * Math.PI * circleRadius;
	const percentToUnits = circumference / 100;
	const currentPercent = Math.round(((value - min) / (max - min)) * 100);
	const gapPercent = 1;

	return (
		<div
			className={cn("relative size-40 text-2xl font-semibold", className)}
			style={
				{
					"--circle-size": "100px",
					"--circumference": circumference,
					"--percent-to-units": percentToUnits,
					"--gap-percent": gapPercent,
					"--offset-factor": "0",
					"--transition-length": `${transitionLength}ms`,
					"--transition-step": `${transitionLength}ms`,
					"--delay": "0s",
					"--percent-to-deg": "3.6deg",
					transform: "translateZ(0)",
				} as React.CSSProperties
			}
		>
			<svg
				fill="none"
				className="size-full"
				strokeWidth="2"
				viewBox="0 0 100 100"
				aria-hidden
				role="presentation"
			>
				<title>Progress ring</title>
				{currentPercent <= 100 - gapPercent && currentPercent >= 0 && (
					<circle
						cx="50"
						cy="50"
						r={circleRadius}
						strokeWidth="10"
						strokeDashoffset="0"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="opacity-100"
						style={
							{
								stroke: gaugeSecondaryColor,
								"--stroke-percent":
									100 - gapPercent - currentPercent,
								"--offset-factor-secondary":
									"calc(1 - var(--offset-factor))",
								strokeDasharray:
									"calc(var(--stroke-percent) * var(--percent-to-units)) var(--circumference)",
								transform:
									"rotate(calc(1turn - 90deg - (var(--gap-percent) * var(--percent-to-deg) * var(--offset-factor-secondary)))) scaleY(-1)",
								transition:
									"all var(--transition-length) ease var(--delay)",
								transformOrigin:
									"calc(var(--circle-size) / 2) calc(var(--circle-size) / 2)",
							} as React.CSSProperties
						}
					/>
				)}
				<circle
					cx="50"
					cy="50"
					r={circleRadius}
					strokeWidth="10"
					strokeDashoffset="0"
					strokeLinecap="round"
					strokeLinejoin="round"
					className="opacity-100"
					style={
						{
							stroke: gaugePrimaryColor,
							"--stroke-percent": currentPercent,
							strokeDasharray:
								"calc(var(--stroke-percent) * var(--percent-to-units)) var(--circumference)",
							transition:
								"var(--transition-length) ease var(--delay),stroke var(--transition-length) ease var(--delay)",
							transitionProperty: "stroke-dasharray,transform",
							transform:
								"rotate(calc(-90deg + var(--gap-percent) * var(--offset-factor) * var(--percent-to-deg)))",
							transformOrigin:
								"calc(var(--circle-size) / 2) calc(var(--circle-size) / 2)",
						} as React.CSSProperties
					}
				/>
			</svg>
			{textVisible && (
				<span
					data-current-value={currentPercent}
					className="animate-in fade-in absolute inset-0 m-auto size-fit delay-[var(--delay)] duration-[var(--transition-length)] ease-linear"
				>
					{currentPercent}
				</span>
			)}
		</div>
	);
}
